import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Chip,
    Dropdown,
    Label,
    ListBox,
    Pagination,
    Typography,
} from '@heroui/react';
import { DataGrid, InlineSelect } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { Icon } from '@iconify/react';
import type { SalesTransaction } from '@/api/models';
import { formatGel, getNodeTestIdProp, getNodeTestProps } from '@/utils';
import {
    formatTransactionId,
    formatTransactionTime,
    getCartUnitsCount,
    getPaymentKind,
    getStatusBadgeKind,
    isDiscounted,
    PAYMENT_META,
    STATUS_BADGE_META,
    TRANSACTION_ROW_ACTIONS,
    TRANSACTIONS_PAGE_SIZE_OPTIONS,
    type TransactionRowAction,
} from '../../transactionsMeta';
import { TransactionDetailModal } from './TransactionDetailModal';

type TransactionsListViewProps = {
    items: SalesTransaction[];
    totalItems: number;
    totalPages: number;
    page: number;
    rowsPerPage: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (next: number) => void;
    onRowsPerPageChange: (next: number) => void;
};

const COLUMN = {
    txn: 'txn',
    seller: 'seller',
    payment: 'payment',
    units: 'units',
    total: 'total',
    status: 'status',
    actions: 'actions',
} as const;

/**
 * The transactions table (render-only) — a bare `<section>` holding a `Card`
 * with the DataGrid and a server-pagination footer (rows-per-page + page
 * navigation), mirroring the bos-app tables. Columns aren't client-sortable: the
 * data is one server page in `transactionDate desc` order, so sorting a single
 * page would mislead. A row's actions dropdown → "View details" opens the modal.
 */
export const TransactionsListView: React.FC<TransactionsListViewProps> = ({
    items,
    totalItems,
    totalPages,
    page,
    rowsPerPage,
    pageWindow,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const { t } = useTranslation('pages');
    const [openId, setOpenId] = React.useState<string | null>(null);

    const openTransaction = React.useMemo(
        () => items.find((item) => item.id === openId) ?? null,
        [items, openId],
    );

    // If the rows change underneath (a page or date change) and the open row is
    // gone, drop the stale id so the modal can't re-open later.
    React.useEffect(() => {
        if (openId !== null && openTransaction === null) {
            setOpenId(null);
        }
    }, [openId, openTransaction]);

    const onRowAction = React.useCallback(
        (id: string, action: TransactionRowAction) => {
            if (action === 'details') {
                setOpenId(id);
            }
        },
        [],
    );

    const columns = React.useMemo<DataGridColumn<SalesTransaction>[]>(
        () => [
            {
                id: COLUMN.txn,
                header: t('transactions.columns.txn'),
                width: 150,
                isRowHeader: true,
                cell: (item) => (
                    <div
                        className="flex flex-col gap-0.5"
                        {...getNodeTestProps(`transactions-row-${item.id}`)}
                    >
                        <span className="font-mono text-xs font-semibold text-accent">
                            {formatTransactionId(item.id)}
                        </span>
                        <span className="text-[11px] text-muted tabular-nums">
                            {formatTransactionTime(item)}
                        </span>
                    </div>
                ),
            },
            {
                id: COLUMN.seller,
                header: t('transactions.columns.seller'),
                cell: (item) => (
                    <span className="truncate text-sm text-foreground">
                        {item.seller.name}
                    </span>
                ),
            },
            {
                id: COLUMN.payment,
                header: t('transactions.columns.payment'),
                width: 160,
                cell: (item) => {
                    const payment = getPaymentKind(item);
                    if (!payment) {
                        return null;
                    }
                    const meta = PAYMENT_META[payment];
                    return (
                        <div className="flex items-center gap-1.5">
                            <Icon
                                className={`size-4 ${meta.color}`}
                                icon={meta.icon}
                            />
                            <span className="text-sm text-foreground">
                                {t(meta.labelKey)}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: COLUMN.units,
                header: t('transactions.columns.units'),
                width: 90,
                align: 'end',
                cell: (item) => (
                    <span className="text-sm text-muted tabular-nums">
                        {getCartUnitsCount(item)}
                    </span>
                ),
            },
            {
                id: COLUMN.total,
                header: t('transactions.columns.total'),
                // Room for a discounted sale's "Discount −₾ 31.50" line.
                width: 160,
                align: 'end',
                cell: (item) => (
                    <div className="flex flex-col items-end gap-0.5 tabular-nums">
                        <span
                            className="text-sm font-semibold text-foreground"
                            {...getNodeTestProps(
                                `transactions-row-total-${item.id}`,
                            )}
                        >
                            {formatGel(item.total)}
                        </span>
                        {isDiscounted(item) ? (
                            <Typography
                                className="inline-flex items-center gap-1 whitespace-nowrap"
                                {...getNodeTestIdProp(
                                    `transactions-row-discount-${item.id}`,
                                )}
                                type="body-xs"
                            >
                                <Icon
                                    className="size-3"
                                    icon="solar:tag-price-linear"
                                />
                                {t('transactions.discountLine', {
                                    amount: formatGel(item.discountTotal),
                                })}
                            </Typography>
                        ) : null}
                    </div>
                ),
            },
            {
                id: COLUMN.status,
                header: t('transactions.columns.status'),
                width: 120,
                cell: (item) => {
                    const badge = STATUS_BADGE_META[getStatusBadgeKind(item)];
                    return (
                        <Chip color={badge.color} size="sm" variant="soft">
                            {t(badge.labelKey)}
                        </Chip>
                    );
                },
            },
            {
                id: COLUMN.actions,
                header: '',
                width: 56,
                align: 'center',
                cell: (item) => (
                    <Dropdown>
                        <Button
                            aria-label={t('transactions.actions.aria')}
                            isIconOnly
                            {...getNodeTestProps(
                                `transactions-row-actions-${item.id}`,
                            )}
                            size="sm"
                            variant="ghost"
                        >
                            <Icon icon="solar:menu-dots-bold" />
                        </Button>
                        <Dropdown.Popover className="min-w-40">
                            <Dropdown.Menu
                                onAction={(key) =>
                                    onRowAction(
                                        item.id,
                                        key as TransactionRowAction,
                                    )
                                }
                            >
                                {TRANSACTION_ROW_ACTIONS.map((action) => (
                                    <Dropdown.Item
                                        id={action.id}
                                        key={action.id}
                                        textValue={t(action.labelKey)}
                                        {...getNodeTestIdProp(
                                            `transactions-row-action-${action.id}-${item.id}`,
                                        )}
                                    >
                                        <Icon icon={action.icon} />
                                        <Label>{t(action.labelKey)}</Label>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown.Popover>
                    </Dropdown>
                ),
            },
        ],
        [t, onRowAction],
    );

    return (
        <section {...getNodeTestProps('transactions-list')}>
            <Card
                className="overflow-hidden p-0"
                {...getNodeTestProps('transactions-card')}
            >
                {/* DataGrid renders its own react-aria id and drops foreign
                    props, so the test hook lives on a wrapper, not the grid. */}
                <div {...getNodeTestProps('transactions-grid')}>
                    <DataGrid<SalesTransaction>
                        aria-label={t('transactions.tableAria')}
                        columns={columns}
                        data={items}
                        getRowId={(item) => item.id}
                        renderEmptyState={() => (
                            <div
                                className="flex flex-col items-center gap-3 py-12 text-muted"
                                {...getNodeTestProps('transactions-empty')}
                            >
                                <Icon
                                    className="size-8 text-muted"
                                    icon="solar:bill-list-bold-duotone"
                                />
                                <span className="text-sm">
                                    {t('transactions.emptyTitle')}
                                </span>
                                <span className="text-xs">
                                    {t('transactions.emptyHint')}
                                </span>
                            </div>
                        )}
                        scrollContainerClassName="max-h-[560px] overflow-y-auto"
                        variant="primary"
                    />
                </div>

                {totalItems > TRANSACTIONS_PAGE_SIZE_OPTIONS[0]! ? (
                    <div
                        className="flex flex-wrap items-center justify-between gap-3 border-t border-border p-3"
                        {...getNodeTestProps('transactions-pagination')}
                    >
                        <div className="flex items-center gap-2 text-xs text-muted">
                            <span>{t('transactions.pagination.perPage')}</span>
                            <InlineSelect
                                aria-label={t(
                                    'transactions.pagination.perPage',
                                )}
                                onChange={(value) =>
                                    value && onRowsPerPageChange(Number(value))
                                }
                                value={String(rowsPerPage)}
                                {...getNodeTestProps('transactions-per-page')}
                            >
                                <InlineSelect.Trigger>
                                    <InlineSelect.Value />
                                    <InlineSelect.Indicator />
                                </InlineSelect.Trigger>
                                <InlineSelect.Popover className="w-24">
                                    <ListBox>
                                        {TRANSACTIONS_PAGE_SIZE_OPTIONS.map(
                                            (size) => (
                                                <ListBox.Item
                                                    id={String(size)}
                                                    key={size}
                                                    textValue={String(size)}
                                                >
                                                    {size}
                                                    <ListBox.ItemIndicator />
                                                </ListBox.Item>
                                            ),
                                        )}
                                    </ListBox>
                                </InlineSelect.Popover>
                            </InlineSelect>
                        </div>
                        {totalPages > 1 ? (
                            <Pagination size="sm">
                                <Pagination.Content>
                                    <Pagination.Item>
                                        <Pagination.Previous
                                            aria-label={t(
                                                'transactions.pagination.previous',
                                            )}
                                            isDisabled={page === 1}
                                            onPress={() =>
                                                onPageChange(page - 1)
                                            }
                                        >
                                            <Pagination.PreviousIcon />
                                        </Pagination.Previous>
                                    </Pagination.Item>
                                    {pageWindow.map((entry, index) =>
                                        entry === 'ellipsis' ? (
                                            <Pagination.Item
                                                key={`transactions-page-ellipsis-${index}`}
                                            >
                                                <Pagination.Ellipsis />
                                            </Pagination.Item>
                                        ) : (
                                            <Pagination.Item key={entry}>
                                                <Pagination.Link
                                                    isActive={entry === page}
                                                    onPress={() =>
                                                        onPageChange(entry)
                                                    }
                                                    {...getNodeTestIdProp(
                                                        `transactions-page-${entry}`,
                                                    )}
                                                >
                                                    {entry}
                                                </Pagination.Link>
                                            </Pagination.Item>
                                        ),
                                    )}
                                    <Pagination.Item>
                                        <Pagination.Next
                                            aria-label={t(
                                                'transactions.pagination.next',
                                            )}
                                            isDisabled={page === totalPages}
                                            onPress={() =>
                                                onPageChange(page + 1)
                                            }
                                        >
                                            <Pagination.NextIcon />
                                        </Pagination.Next>
                                    </Pagination.Item>
                                </Pagination.Content>
                            </Pagination>
                        ) : null}
                    </div>
                ) : null}
            </Card>

            <TransactionDetailModal
                isOpen={openTransaction !== null}
                onClose={() => setOpenId(null)}
                transaction={openTransaction}
            />
        </section>
    );
};
